import styled from 'styled-components'

export const LABEL_WIDTH = 200

export const ChartRoot = styled.div`
  position: relative;
  width: 100%;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  padding-top: 14px;
`

export const Row = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_WIDTH}px minmax(0, 1fr);
  align-items: center;
  min-height: 46px;
  position: relative;
  z-index: 1;
  outline: none;

  /* The hover card lives inside the row; the row must sit above the other rows and the
     final-estimate band and line (z-index 2 and 3) while it is shown. */
  &:hover,
  &:focus-within {
    z-index: 30;
  }
`

export const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding-right: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const Dot = styled.span<{ $color: string }>`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

export const Bar = styled.div`
  position: relative;
  height: 30px;
`

export const RangeBar = styled.div<{ $color: string }>`
  position: absolute;
  top: 9px;
  height: 12px;
  border-radius: 6px;
  background: ${({ $color }) => $color};
  opacity: 0.22;
`

export const ValueMarker = styled.div<{ $color: string }>`
  position: absolute;
  top: 4px;
  width: 3px;
  height: 22px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`

export const ValueLabel = styled.span<{ $color: string }>`
  position: absolute;
  top: -12px;
  transform: translateX(-50%);
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color }) => $color};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`

export const EdgeLabel = styled.span<{ $side: 'min' | 'max' }>`
  position: absolute;
  top: 9px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transform: ${({ $side }) => ($side === 'min' ? 'translateX(-100%)' : 'none')};
`

export const FinalBand = styled.div`
  position: absolute;
  top: 14px;
  bottom: 28px;
  left: calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * var(--l) / 100);
  width: calc((100% - ${LABEL_WIDTH}px) * var(--w) / 100);
  background: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.06;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  z-index: 2;
  pointer-events: none;
`

export const FinalLine = styled.div`
  position: absolute;
  top: 14px;
  bottom: 28px;
  left: calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * var(--l) / 100);
  width: 0;
  border-left: 2px dashed ${({ theme }) => theme.colors.text.primary};
  opacity: 0.5;
  z-index: 3;
  pointer-events: none;
`

/**
 * A narrow strip over the final valuation line that takes the hover and holds its card.
 * It sits above the rows (which rise to z-index 30 while hovered) so the line can be
 * reached anywhere along its height.
 */
export const FinalHitArea = styled.div`
  position: absolute;
  top: 14px;
  bottom: 28px;
  left: calc(
    ${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * var(--l) / 100 - 7px
  );
  width: 14px;
  z-index: 31;
  cursor: default;
  outline: none;
`

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`

export const LegendSwatch = styled.span`
  flex: 0 0 auto;
  width: 14px;
  border-top: 2px dashed ${({ theme }) => theme.colors.text.primary};
  opacity: 0.5;
`

export const Axis = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_WIDTH}px minmax(0, 1fr);
  margin-top: ${({ theme }) => theme.spacing.small};
`

export const AxisTrack = styled.div`
  position: relative;
  height: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  container-type: inline-size;
  container-name: axis;
`

/** Every other label hides when the track is too short for all of them to stay apart. */
export const Tick = styled.span`
  position: absolute;
  top: 4px;
  transform: translateX(-50%);
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-variant-numeric: tabular-nums;

  @container axis (max-width: 440px) {
    &:nth-child(even) {
      display: none;
    }
  }
`

export const HoverCard = styled.div<{ $align: 'start' | 'center' | 'end' }>`
  position: absolute;
  top: calc(100% + 2px);
  min-width: 220px;
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  text-align: left;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transform: ${({ $align }) =>
    $align === 'start'
      ? 'none'
      : $align === 'end'
        ? 'translateX(-100%)'
        : 'translateX(-50%)'};
  z-index: 20;
  pointer-events: none;
`

export const HoverEyebrow = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.uppercase};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const HoverName = styled.div`
  margin: ${({ theme }) => theme.spacing.xsmall} 0
    ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`

export const HoverBox = styled.div<{ $tone: 'value' | 'range' }>`
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme, $tone }) =>
    $tone === 'value'
      ? theme.colors.primaryLight
      : theme.colors.background.secondary};

  & + & {
    margin-top: ${({ theme }) => theme.spacing.small};
  }
`

export const HoverValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
`

export const HoverSub = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const HoverLine = styled.div`
  b {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
`
